import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    LinearProgress,
    Chip
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { Reimbursement, REIMBURSEMENT_STATUS_COLORS, REIMBURSEMENT_STATUS_LABELS } from '../../types/collaborator';
import { createReimbursement, getReimbursements } from '../../services/collaborator';
import { openUploadedFile } from '../../services/fileService';
import { formatCurrency } from '../../utils/format';
import { formatDateBR } from '../../utils/date';

const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;

const ReimbursementsSection: React.FC = () => {
    const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [receipt, setReceipt] = useState<File | null>(null);
    // Data local (toISOString usa UTC: à noite no Brasil já seria o dia seguinte)
    const today = format(new Date(), 'yyyy-MM-dd');
    const [expenseDate, setExpenseDate] = useState(today);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const fetchReimbursements = async () => {
        try {
            setLoading(true);
            const data = await getReimbursements();
            setReimbursements(data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar reembolsos. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReimbursements();
    }, []);

    const openNew = () => {
        setDescription('');
        setAmount('');
        setReceipt(null);
        setExpenseDate(today);
        setSubmitError(null);
        setOpenDialog(true);
    };

    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        e.target.value = ''; // permite escolher o mesmo arquivo de novo
        if (file && file.size > MAX_RECEIPT_BYTES) {
            setSubmitError('O comprovante deve ter no máximo 5 MB.');
            return;
        }
        setSubmitError(null);
        setReceipt(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!description || !amount || !expenseDate) {
            return;
        }

        try {
            setSaving(true);
            setSubmitError(null);
            await createReimbursement({
                description,
                amount: parseFloat(amount),
                date: expenseDate,
                receipt,
            });
            setOpenDialog(false);
            fetchReimbursements();
        } catch (err: any) {
            // Mantém o diálogo aberto com a mensagem do backend
            setSubmitError(err?.response?.data?.error || 'Erro ao criar solicitação de reembolso. Por favor, tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <LinearProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                <Typography variant="h6">
                    Reembolsos
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={openNew}
                >
                    Nova Solicitação
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Comprovante</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reimbursements.map((reimbursement) => (
                            <TableRow key={reimbursement.id}>
                                <TableCell>{formatDateBR(reimbursement.date)}</TableCell>
                                <TableCell>
                                    {reimbursement.description}
                                    {reimbursement.review_notes && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Parecer do RH: {reimbursement.review_notes}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCurrency(reimbursement.amount)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={REIMBURSEMENT_STATUS_LABELS[reimbursement.status] || reimbursement.status}
                                        color={REIMBURSEMENT_STATUS_COLORS[reimbursement.status] || 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {reimbursement.receipt_url && (
                                        <Button
                                            size="small"
                                            onClick={() => openUploadedFile(reimbursement.receipt_url!)
                                                .catch(() => setError('Não foi possível abrir o comprovante.'))}
                                        >
                                            Ver Comprovante
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {reimbursements.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Nenhuma solicitação de reembolso encontrada.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Nova Solicitação de Reembolso</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 0 }}>
                            {submitError && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{submitError}</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição"
                                    multiline
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Valor (R$)"
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    inputProps={{
                                        step: "0.01",
                                        min: "0.01"
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data da despesa"
                                    type="date"
                                    value={expenseDate}
                                    onChange={(e) => setExpenseDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: today }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <input
                                    id="receipt-file"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    style={{ display: 'none' }}
                                    onChange={handleFile}
                                />
                                <label htmlFor="receipt-file">
                                    <Button component="span" variant="outlined" startIcon={<AttachFileIcon />}>
                                        {receipt ? 'Trocar comprovante' : 'Anexar comprovante'}
                                    </Button>
                                </label>
                                <Typography variant="caption" color={receipt ? 'success.main' : 'text.secondary'} display="block" mt={1}>
                                    {receipt ? `Arquivo: ${receipt.name}` : 'PDF, JPG ou PNG, até 5 MB'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={saving || !description || !amount || !expenseDate}
                        >
                            Solicitar Reembolso
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default ReimbursementsSection;
